import React, { useEffect, useState, useCallback, useRef } from 'react';
import { clearMockPartnerEvents, loadMockPartnerEvents } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { resolveNotificationImageUrl } from '../lib/utils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface MockEvent {
  id: number;
  timestamp: string;
  sourceSystem: string;
  organizationId: string;
  alertType: string;
  clientId: string;
  clientName: string;
  severity: string;
  message: string;
  accepted: boolean;
  error: string | null;
  payload: any;
}

function severityClass(sev: string): string {
  switch (sev) {
    case 'high': return 'text-red-400';
    case 'medium': return 'text-amber-400';
    case 'low': return 'text-blue-400';
    default: return 'text-emerald-400';
  }
}

export const MockPartnerMonitor: React.FC = () => {
  const [events, setEvents] = useState<MockEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoScroll, setAutoScroll] = useState(true);
  const [orgFilter, setOrgFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [organizations, setOrganizations] = useState<string[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [stats, setStats] = useState({ total: 0, accepted: 0, rejected: 0 });
  const [isClearing, setIsClearing] = useState(false);

  const seenIds = useRef(new Set<number>());
  const audioCtxRef = useRef<AudioContext | null>(null);
  const evtSourceRef = useRef<EventSource | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const playBeep = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      gain.gain.value = 0.15;
      osc.start();
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
      osc.stop(ctx.currentTime + 0.15);
    } catch {}
  }, [soundEnabled]);

  const addOrg = useCallback((orgId: string) => {
    if (!orgId || orgId === 'unknown') return;
    setOrganizations(prev => prev.includes(orgId) ? prev : [...prev, orgId]);
  }, []);

  const addSource = useCallback((source: string) => {
    if (!source || source === 'unknown') return;
    setSources(prev => prev.includes(source) ? prev : [...prev, source].sort());
  }, []);

  const resetMonitorState = useCallback(() => {
    setEvents([]);
    setOrganizations([]);
    setSources([]);
    seenIds.current.clear();
    setStats({ total: 0, accepted: 0, rejected: 0 });
  }, []);

  const addEvent = useCallback((event: MockEvent, isLive: boolean) => {
    const normalized = {
      ...event,
      sourceSystem: event.sourceSystem || event.payload?.source || 'unknown',
    };

    if (normalized.id != null && seenIds.current.has(normalized.id)) return;
    if (normalized.id != null) seenIds.current.add(normalized.id);

    addOrg(normalized.organizationId);
    addSource(normalized.sourceSystem);

    setEvents(prev => {
      const next = [normalized, ...prev];
      return next.length > 500 ? next.slice(0, 500) : next;
    });

    setStats(prev => ({
      total: prev.total + 1,
      accepted: prev.accepted + (event.accepted ? 1 : 0),
      rejected: prev.rejected + (event.accepted ? 0 : 1),
    }));

    if (isLive) playBeep();
  }, [addOrg, addSource, playBeep]);

  const loadInitialEvents = useCallback(async (filterOrgId?: string) => {
    try {
      const data = await loadMockPartnerEvents({
        limit: 200,
        organizationId: filterOrgId || undefined,
      });
      const fetched: MockEvent[] = data.events || [];

      seenIds.current.clear();
      let accepted = 0;
      let rejected = 0;
      fetched.forEach(e => {
        if (e.id != null) seenIds.current.add(e.id);
        addOrg(e.organizationId);
        addSource(e.sourceSystem || e.payload?.source || 'unknown');
        if (e.accepted) accepted++;
        else rejected++;
      });
      setEvents(fetched);
      setStats({ total: fetched.length, accepted, rejected });
    } catch (err) {
      console.error('[MockPartnerMonitor] Failed to load events:', err);
    }
  }, [addOrg, addSource]);

  // SSE connection. EventSource cannot set headers, so pass the JWT as a
  // short-lived ?token= query param; the backend accepts it and verifies the
  // same signature as Authorization: Bearer. See F8.
  const token = useAuthStore((s) => s.token);
  useEffect(() => {
    if (!token) return;
    const url = `${API_BASE_URL}/api/v2/events/stream?token=${encodeURIComponent(token)}`;
    const source = new EventSource(url);
    evtSourceRef.current = source;

    source.onopen = () => {
      setConnected(true);
    };

    source.onmessage = (e) => {
      try {
        const parsed = JSON.parse(e.data);
        if (parsed.type === 'cleared') {
          setOrgFilter('');
          setSourceFilter('');
          resetMonitorState();
          return;
        }
        if (parsed.type === 'connected' || !parsed.id) return;
        addEvent(parsed, true);
      } catch {}
    };

    source.onerror = () => {
      setConnected(false);
    };

    return () => {
      source.close();
      evtSourceRef.current = null;
    };
  }, [addEvent, resetMonitorState, token]);

  // Initial load
  useEffect(() => {
    loadInitialEvents();
  }, [loadInitialEvents]);

  // Periodic missed-event fetch
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const data = await loadMockPartnerEvents({ limit: 200 });
        const fetched: MockEvent[] = data.events || [];
        const newEvents = fetched.filter(e => e.id != null && !seenIds.current.has(e.id));
        if (newEvents.length > 0) {
          newEvents.sort((a, b) => a.id - b.id);
          newEvents.forEach(e => addEvent(e, true));
        }
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [addEvent]);

  const handleOrgFilter = (value: string) => {
    setOrgFilter(value);
    loadInitialEvents(value);
  };

  const handleSourceFilter = (value: string) => {
    setSourceFilter(value);
  };

  const clearEvents = async () => {
    setIsClearing(true);
    try {
      await clearMockPartnerEvents();
      setOrgFilter('');
      setSourceFilter('');
      resetMonitorState();
    } catch (err) {
      console.error('[MockPartnerMonitor] Failed to clear events:', err);
    } finally {
      setIsClearing(false);
    }
  };

  const filteredEvents = events.filter(e => {
    const matchesOrg = orgFilter ? String(e.organizationId) === orgFilter : true;
    const matchesSource = sourceFilter ? String(e.sourceSystem || e.payload?.source || 'unknown') === sourceFilter : true;
    return matchesOrg && matchesSource;
  });

  return (
    <div className="min-h-screen bg-[#0f172a] text-[#e2e8f0] flex flex-col" style={{ margin: '-1.5rem', minHeight: 'calc(100vh - 64px)' }}>
      {/* Header */}
      <div className="bg-[#1e293b] px-6 py-4 border-b border-[#334155] flex justify-between items-center">
        <h1 className="text-lg font-semibold">Mock PARTNER — Live Event Monitor</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={clearEvents}
            disabled={isClearing}
            className="px-3 py-1.5 bg-[#334155] text-[#e2e8f0] rounded text-xs hover:bg-[#475569] transition-colors"
          >
            {isClearing ? 'Clearing...' : 'Clear'}
          </button>
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            className="px-3 py-1.5 bg-[#334155] text-[#e2e8f0] rounded text-xs hover:bg-[#475569] transition-colors"
          >
            Auto-scroll: {autoScroll ? 'ON' : 'OFF'}
          </button>
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="px-3 py-1.5 bg-[#334155] text-[#e2e8f0] rounded text-xs hover:bg-[#475569] transition-colors"
          >
            Sound: {soundEnabled ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-[#1e293b] px-6 py-3 border-b border-[#334155] flex items-center gap-6 text-[13px]">
        <div className="flex items-center gap-1.5">
          <span
            className={`w-2 h-2 rounded-full ${connected ? 'bg-emerald-500' : 'bg-red-500'}`}
          />
          <span className="text-[#94a3b8]">SSE:</span>
          <span>{connected ? 'Connected' : 'Disconnected'}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[#94a3b8]">Total:</span>
          <span className="font-semibold tabular-nums">{stats.total}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[#94a3b8]">Accepted:</span>
          <span className="font-semibold tabular-nums text-emerald-400">{stats.accepted}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[#94a3b8]">Rejected:</span>
          <span className="font-semibold tabular-nums text-red-400">{stats.rejected}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[#94a3b8]">Organization:</span>
          <select
            value={orgFilter}
            onChange={(e) => handleOrgFilter(e.target.value)}
            className="bg-[#0f172a] border border-[#334155] text-[#e2e8f0] px-2 py-1 rounded text-xs min-w-[100px]"
          >
            <option value="">All</option>
            {organizations.map(org => (
              <option key={org} value={org}>{org}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[#94a3b8]">Source:</span>
          <select
            value={sourceFilter}
            onChange={(e) => handleSourceFilter(e.target.value)}
            className="bg-[#0f172a] border border-[#334155] text-[#e2e8f0] px-2 py-1 rounded text-xs min-w-[120px]"
          >
            <option value="">All</option>
            {sources.map(source => (
              <option key={source} value={source}>{source}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Events */}
      <div ref={containerRef} className="flex-1 overflow-y-auto p-4 space-y-2.5">
        {filteredEvents.length === 0 && (
          <div className="text-center py-16 text-[#475569]">
            <h2 className="text-base mb-2">Waiting for events...</h2>
            <p className="text-sm">Events will appear here as they are delivered by the PARTNER integration.</p>
          </div>
        )}

        {filteredEvents.map((event) => {
          const imgUrl = resolveNotificationImageUrl(event.payload?.notification?.imageUrl);
          return (
            <div
              key={event.id}
              className={`bg-[#1e293b] border border-[#334155] rounded-lg p-3.5 flex gap-3.5 transition-colors ${
                event.accepted ? 'border-l-[3px] border-l-emerald-500' : 'border-l-[3px] border-l-red-500'
              }`}
            >
              {imgUrl && (
                <img
                  src={imgUrl}
                  alt={event.alertType}
                  loading="lazy"
                  className="flex-shrink-0 w-[160px] sm:w-[190px] lg:w-[220px] h-auto rounded-md object-contain bg-[#0f172a]"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-sm">
                    #{event.id} {event.alertType}
                  </span>
                  <span className="text-xs text-[#64748b] tabular-nums">
                    {new Date(event.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <div className="flex gap-2.5 flex-wrap text-xs text-[#94a3b8]">
                  <span className="bg-[#334155] px-2 py-0.5 rounded font-semibold">
                    Org: {event.organizationId}
                  </span>
                  <span className="bg-[#0f172a] px-2 py-0.5 rounded font-semibold text-cyan-300">
                    Source: {event.sourceSystem || event.payload?.source || 'unknown'}
                  </span>
                  <span className="bg-[#0f172a] px-2 py-0.5 rounded">
                    Client: {event.clientId || 'N/A'} ({event.clientName || 'N/A'})
                  </span>
                  <span className={`bg-[#0f172a] px-2 py-0.5 rounded ${severityClass(event.severity)}`}>
                    Severity: {event.severity || 'N/A'}
                  </span>
                  <span className="bg-[#0f172a] px-2 py-0.5 rounded">
                    {event.accepted ? '✓ Accepted' : `✗ Rejected: ${event.error || ''}`}
                  </span>
                </div>
                {event.message && (
                  <div className="mt-2 text-[13px] text-[#cbd5e1] leading-relaxed">
                    {event.message}
                  </div>
                )}
                <details className="mt-2">
                  <summary className="text-xs text-[#64748b] cursor-pointer hover:text-[#94a3b8]">
                    View payload
                  </summary>
                  <pre className="mt-1.5 bg-[#0f172a] p-2.5 rounded text-[11px] overflow-x-auto max-h-[300px] text-[#94a3b8]">
                    {JSON.stringify(event.payload, null, 2)}
                  </pre>
                </details>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
